import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
    static targets = ['strip', 'item', 'prevBtn', 'nextBtn'];

    connect() {
        this.scrollToActive();
        this.updateButtons();
        this.scrollRAF = null;
        this.resizeRAF = null;
        this.stripTarget.addEventListener('scroll', () => this.handleScroll());
        window.addEventListener('resize', () => this.handleResize());
    }

    disconnect() {
        window.removeEventListener('resize', () => this.handleResize());
    }

    handleScroll() {
        if (this.scrollRAF) {
            return;
        }
        this.scrollRAF = requestAnimationFrame(() => {
            this.updateButtons();
            this.scrollRAF = null;
        });
    }

    handleResize() {
        if (this.resizeRAF) {
            return;
        }
        this.resizeRAF = requestAnimationFrame(() => {
            this.updateButtons();
            this.resizeRAF = null;
        });
    }

    scrollToActive() {
        // Try to find active cell first, then fall back to active title/item
        const activeElement = this.element.querySelector('.calendar-cell.-current') ||
                              this.element.querySelector('.calendar-strip__item__title.-current') ||
                              this.element.querySelector('.calendar-strip__item.-current');

        if (activeElement) {
            activeElement.scrollIntoView({
                behavior: 'instant',
                block: 'nearest',
                inline: activeElement.classList.contains('calendar-cell') ? 'center' : 'start',
            });
        }
    }

    next() {
        const current = this.getCurrentState();
        const stripRect = this.stripTarget.getBoundingClientRect();
        const currentItemRect = current.item.getBoundingClientRect();

        // Check if current item extends beyond viewport
        const itemOverflows = currentItemRect.right > stripRect.right + 10;

        if (itemOverflows) {
            // Scroll by viewport width
            this.stripTarget.scrollBy({
                left: stripRect.width,
                behavior: 'smooth',
            });
        } else if (current.index < this.itemTargets.length - 1) {
            // Go to next item - scroll only the container, not the page
            const nextItem = this.itemTargets[current.index + 1];
            const nextItemRect = nextItem.getBoundingClientRect();
            const scrollAmount = nextItemRect.left - stripRect.left;

            this.stripTarget.scrollBy({
                left: scrollAmount,
                behavior: 'smooth',
            });
        }
    }

    prev() {
        const current = this.getCurrentState();
        const stripRect = this.stripTarget.getBoundingClientRect();
        const currentItemRect = current.item.getBoundingClientRect();

        // Check if we're viewing the middle/end of current item (not at its start)
        const notAtItemStart = currentItemRect.left < stripRect.left - 10;

        if (notAtItemStart) {
            // Scroll back by viewport width
            this.stripTarget.scrollBy({
                left: -stripRect.width,
                behavior: 'smooth',
            });
        } else if (current.index > 0) {
            // Go to previous item - scroll only the container, not the page
            const prevItem = this.itemTargets[current.index - 1];
            const prevItemRect = prevItem.getBoundingClientRect();
            const scrollAmount = prevItemRect.left - stripRect.left;

            this.stripTarget.scrollBy({
                left: scrollAmount,
                behavior: 'smooth',
            });
        }
    }

    getCurrentState() {
        const stripRect = this.stripTarget.getBoundingClientRect();
        const items = this.itemTargets;
        let currentIndex = 0;
        let currentItem = items[0];

        // Find current visible item - check which item is most visible
        for (let i = items.length - 1; i >= 0; i--) {
            const itemRect = items[i].getBoundingClientRect();
            // If item's left edge is at or before the strip's left edge (visible)
            if (itemRect.left <= stripRect.left + 10) {
                currentIndex = i;
                currentItem = items[i];
                break;
            }
        }

        const isLastItem = currentIndex === items.length - 1;
        const lastItem = items[items.length - 1];
        const lastItemRect = lastItem.getBoundingClientRect();

        // Check if last item overflows container
        const lastItemOverflows = lastItemRect.width > stripRect.width;

        // Check if we're at last item aligned to start
        const isLastItemStart = isLastItem && Math.abs(lastItemRect.left - stripRect.left) < 10;

        // Check if we're at last item aligned to end
        const isLastItemEnd = isLastItem && Math.abs(lastItemRect.right - stripRect.right) < 10;

        return {
            index: currentIndex,
            item: currentItem,
            isLastItem,
            isLastItemStart,
            isLastItemEnd,
            lastItemOverflows,
        };
    }

    updateButtons() {
        const state = this.getCurrentState();

        // Disable prev only if at first item start
        if (this.hasPrevBtnTarget) {
            this.prevBtnTarget.disabled = state.index === 0;
        }

        // Disable next if at last item end (or last item doesn't overflow)
        if (this.hasNextBtnTarget) {
            const atEnd = state.isLastItemEnd || (state.isLastItem && !state.lastItemOverflows);
            this.nextBtnTarget.disabled = atEnd;
        }
    }
}
